import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserBadge from './UserBadge';
import {
  FaEye,
  FaTrash,
  FaMinus,
  FaEdit,
  FaSave,
  FaUsers,
  FaPlus,
  FaUsersCog,
  FaHome,
  FaFutbol
} from 'react-icons/fa';

import { MdCancel } from 'react-icons/md';

const EditGame = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchGroupName = async (groupId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/groups/my-admin-groups', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const group = data.adminGroups.find((g) => g.id === groupId);
      if (!group) throw new Error('Not admin of this group');
      setGroups(data.adminGroups);
      setGroupName(group.name);
    } catch {
      alert('You are not authorized to edit this game.');
      navigate('/games');
    }
  };

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/games/${gameId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch game data');

        const json = await response.json();
        const data = json.game || json;
        const datetime = new Date(data.game_datetime);
        console.log('📥 Raw game_datetime from backend:', data.game_datetime);
        console.log('🕒 Parsed local datetime (from UTC):', datetime);

        const game_date = datetime.toISOString().split('T')[0];
        const game_time = datetime.toTimeString().slice(0, 5);
        console.log('📅 game_date:', game_date, '⏰ game_time:', game_time);

        setFormData({
          description: data.description,
          game_date,
          game_time,
          venue: data.venue || '',
          cost: data.cost || '',
          opponent: data.opponent || '',
          colour: data.colour || '',
          notes: data.notes || '',
          group_id: data.group_id,
          max_players: data.max_players,
          rsvp_cutoff: data.rsvp_cutoff || 0,
        });

        await fetchGroupName(data.group_id);
        setLoading(false);
      } catch (err) {
        alert('Error loading game.');
        navigate('/games');
      }
    };

    fetchGameData();
  }, [gameId, navigate]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };


 const handleSubmit = async (e) => {
  e.preventDefault();

  if (submitting) return;

  if (
    !formData.description ||
    !formData.game_date ||
    !formData.game_time ||
    !formData.max_players
  ) {
    alert('Please fill in all mandatory fields.');
    return;
  }

  setSubmitting(true);

  // 👇 Build local date object (user entered time is in local time)
  const [year, month, day] = formData.game_date.split('-').map(Number);
  const [hour, minute] = formData.game_time.split(':').map(Number);
  const localDate = new Date(year, month - 1, day, hour, minute); // 🕒 local time

  console.log("📍 Local date (in user's time):", localDate);

  // 👇 Convert to ISO string (UTC)
  const game_datetime = localDate.toISOString();
  console.log("🌍 UTC datetime to send to backend:", game_datetime);

  const updatedData = {
    ...formData,
    game_datetime,
    max_players: parseInt(formData.max_players),
    rsvp_cutoff: parseInt(formData.rsvp_cutoff) || 0,
  };

  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`/api/games/${gameId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatedData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      alert(`Update failed: ${errorData.error}`);
      return;
    }

    alert('Game updated successfully!');
    navigate('/games');
} catch (err) {
    console.error(err);
    alert('Error updating game.');
  } finally {
    setSubmitting(false);
  }
};

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const getCurrentTime = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    return now.toTimeString().slice(0, 5);
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        times.push(`${hour}:${minute}`);
      }
    }
    return times;
  };

  if (loading || !formData) return <p>Loading...</p>;

  return (
    <div style={{ padding: '20px', position: 'relative' }}>
      <UserBadge />
      <h2>Edit Game</h2>
      <form onSubmit={handleSubmit}>
        <p><strong>Group:</strong> {groupName}</p>

        <input
          type="text"
          name="description"
          placeholder="Game Description"
          value={formData.description}
          onChange={handleChange}
          required
          style={{ marginBottom: '10px', display: 'block' }}
        />

        <input
          type="date"
          name="game_date"
          value={formData.game_date}
          onChange={handleChange}
          required
          min={getTodayDate()}
          style={{ marginBottom: '10px', display: 'block' }}
        />

        <select name="game_time" value={formData.game_time} onChange={handleChange} required>
          <option value="">Select Game Time</option>
          {generateTimeOptions().map((time) => {
            const isToday = formData.game_date === getTodayDate();
            const isPast = isToday && time < getCurrentTime();
            return (
              <option key={time} value={time} disabled={isPast}>
                {time}
              </option>
            );
          })}
        </select>

        {['venue', 'cost', 'opponent', 'colour', 'notes'].map((field) => (
          <input
            key={field}
            type="text"
            name={field}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
            value={formData[field]}
            onChange={handleChange}
            style={{ marginBottom: '10px', display: 'block' }}
          />
        ))}

        <input
          type="number"
          name="max_players"
          placeholder="Max Players"
          value={formData.max_players}
          onChange={handleChange}
          min="1"
          required
          style={{ marginBottom: '10px', display: 'block' }}
        />

        <input
          type="number"
          name="rsvp_cutoff"
          placeholder="RSVP Cutoff (hours before game)"
          value={formData.rsvp_cutoff}
          onChange={handleChange}
          min="0"
          style={{ marginBottom: '10px', display: 'block' }}
        />

<div className="button-group" style={{ marginTop: '10px' }}>
<button type="submit" className="btn pastel-green" style={{ marginRight: '10px' }} disabled={submitting}>
  <FaSave /> {submitting ? 'Updating...' : 'Update Game'}
</button>
  <button type="button" onClick={() => navigate(-1)} className="btn pastel-red">
    <MdCancel />
    Cancel
  </button>
</div>

      </form>
    </div>
  );
};

export default EditGame;
